import { useMemo } from 'react'

import { useAppSelector } from 'redux/toolkit/hooks'
import { isSuccess } from 'redux/toolkit/api'

import IconAlertSuccess from 'assets/images/icons/icon_alert_success.svg'
import IconAlertInfo from 'assets/images/icons/icon_alert_info.svg'
import IconAlertWarning from 'assets/images/icons/icon_alert_warning.svg'
import {
  RecipientTable,
  useMessage
} from 'components/libs/message/messageDetails/messageDetailsContent/messageDetailsContentHeader/useMessage'
import { Message } from 'types/Messages'
import appConfig from 'config/appConfig'
import { useMessageLogRights } from 'components/libs/userRights/pages/useMessageLogRights'

export interface State {
  message: Message | undefined
  isGetMessageSuccess: boolean
  reason: string
  determinationColor: string
  determinationIcon: typeof IconAlertSuccess | typeof IconAlertInfo | typeof IconAlertWarning
  subject: string
  from: string
  to: string
  date: string
  id: string
  ip: string
  recipientTable: RecipientTable
  cantViewBlocked: boolean
  cantViewQuarantined: boolean
  isDownloadAttachmentEnabled: boolean | undefined
  forensicsLink: string | undefined
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface EventHandlers {}

export type MessageDetailsContentHeaderLogic = [State, EventHandlers]

export interface MessageDetailsContentHeaderConfig {
  text: (id: string) => string
}

export const useMessageDetailsContentHeaderLogic = ({
  text
}: MessageDetailsContentHeaderConfig): MessageDetailsContentHeaderLogic => {
  const { message, isGetMessageSuccess, hasForensicsAccess, bccAccountId } = useAppSelector(_store => ({
    message: _store.mstore.message,
    isGetMessageSuccess: isSuccess(_store.mstore.getMessageApiStatus),
    hasForensicsAccess: !!_store.settings.accountPermissions?.hasForensicsAccess,
    bccAccountId: _store.auth.accessTokenObject?.bccAccountId
  }))
  const [
    {
      reason,
      determinationColor,
      determinationIcon,
      subject,
      from,
      to,
      date,
      id,
      ip,
      recipientTable,
      cantViewBlocked,
      cantViewQuarantined,
      isDownloadAttachmentEnabled
    }
  ] = useMessage({
    message,
    text
  })
  const { canDownloadAttachments } = useMessageLogRights()

  const forensicsLink = useMemo(() => {
    if (!hasForensicsAccess || !message || !bccAccountId) {
      return undefined
    }
    const searchParams = new URLSearchParams({
      email: message.fields.headerFrom || message.fields.envelopeFrom,
      subject: message.fields.subject,
      bccId: bccAccountId
    })
    return `https://${appConfig.LINKS.FORENSICS_HOSTNAME}/new-incident?${searchParams.toString()}`
  }, [bccAccountId, hasForensicsAccess, message])

  return useMemo(
    () => [
      {
        message,
        isGetMessageSuccess,
        reason,
        determinationColor,
        determinationIcon,
        subject,
        from,
        to,
        date,
        id,
        ip,
        recipientTable,
        cantViewBlocked,
        cantViewQuarantined,
        isDownloadAttachmentEnabled: isDownloadAttachmentEnabled && canDownloadAttachments,
        forensicsLink
      },
      {}
    ],
    [
      message,
      isGetMessageSuccess,
      reason,
      determinationColor,
      determinationIcon,
      subject,
      from,
      to,
      date,
      id,
      ip,
      recipientTable,
      cantViewBlocked,
      cantViewQuarantined,
      isDownloadAttachmentEnabled,
      forensicsLink,
      canDownloadAttachments
    ]
  )
}
